import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faBolt, faLeaf
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/EnergyGrid-Process.gif";
import vis from "../../assets/images/use-cases/EnergyGrid-Dashboard.gif";

const EnergyGridLoadBalancing = () => {
    return (
        <>
            <SEO
                title="Energy Grid Load Balancing: AI Digital Twin for Utilities | Nebulyx AI"
                description="Model your energy grid as connected generation, storage, and demand nodes. Simulate peak load scenarios and renewable intermittency offline to optimise supply mix and maintain grid stability."
                keywords="AI digital twin, energy grid, load balancing, peak demand, renewable energy, grid stability, utilities, power generation"
                canonicalUrl="https://nebulyx.ai/use-cases/energy-grid-load-balancing"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faBolt}/>
                                            &nbsp;
                                            Utilities
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How do we handle peak demand without blackouts?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Model your energy grid as connected generation, storage, and demand nodes. Simulate peak load scenarios and renewable intermittency offline-before grid stress events occur.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faBolt} className="stat-icon"/>
                                <span className="stat-value">18%</span>
                                <span className="stat-label">Cost Reduction</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faLeaf} className="stat-icon"/>
                                <span className="stat-value">+24%</span>
                                <span className="stat-label">Renewable Integration</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">99.9%</span>
                                <span className="stat-label">Grid Stability</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Unpredictable renewable generation</li>
                                            <li>Peak demand spikes risk blackouts</li>
                                            <li>Expensive spinning reserves</li>
                                            <li>No safe way to test grid scenarios</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Build a digital twin of your grid topology</li>
                                            <li>Model generation, storage, and demand nodes</li>
                                            <li>Overlay economic dispatch algorithms</li>
                                            <li>Simulate stress scenarios offline</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Optimise generation mix for lowest cost</li>
                                            <li>Maximise renewable integration safely</li>
                                            <li>Pre-plan demand response triggers</li>
                                            <li>Reduce blackout risk through simulation</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connect grid telemetry, model generation and demand as a network, overlay dispatch optimisation, then simulate stress scenarios to find the optimal supply mix.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Data</strong>
                                    <span>SCADA telemetry, generation fleet, demand forecasts, market prices</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Model the Grid</strong>
                                    <span>Map substations, transmission lines, generators, and storage as nodes</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Overlay Analytics</strong>
                                    <span>Economic dispatch, stability scoring, reserve margin tracking</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Scenario Analysis</strong>
                                    <span>Simulate heatwaves, wind drops, plant outages, demand spikes</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Connects SCADA systems, generation fleet data, demand forecasts, and market pricing to build a comprehensive grid model.
                                            Economic dispatch algorithms find the lowest-cost generation mix that meets demand while respecting transmission constraints,
                                            enabling offline simulation of stress scenarios before they impact grid stability.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Grid Control Workbench</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Visualises real-time supply vs demand balance with generation mix breakdown by source.
                                            Operators can inject simulated stress scenarios-heatwaves, wind drops, plant outages-and watch the dispatch algorithm
                                            rebalance the grid, identifying optimal demand response triggers before actual grid stress events occur.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>SCADA Grid Telemetry</li>
                                            <li>Generation Fleet Output</li>
                                            <li>Renewable Forecasts</li>
                                            <li>Demand Patterns by Zone</li>
                                            <li>Wholesale Market Prices</li>
                                            <li>Transmission Constraints</li>
                                            <li>Weather Correlations</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Economic dispatch optimisation</li>
                                            <li>Peak demand forecasting</li>
                                            <li>Renewable curtailment prediction</li>
                                            <li>Grid frequency stability scoring</li>
                                            <li>Carbon intensity tracking</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Heatwave demand surge (+35%)</li>
                                            <li>Wind generation drop (85%↓)</li>
                                            <li>Gas plant outage</li>
                                            <li>Peak evening demand</li>
                                            <li>Renewable curtailment thresholds</li>
                                            <li>Demand response activation</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Potential Discovery</h3>
                                    <p>
                                        By modelling the grid as a digital twin and running stress scenario simulations offline,
                                        operators could discover that shifting 15% of industrial load to off-peak hours combined with
                                        pre-positioning battery reserves would handle a 35% heatwave demand surge without blackouts.
                                        Simulation may reveal that <strong>optimising the generation dispatch sequence could reduce costs by 18%</strong> while
                                        increasing renewable integration from 28% to 52%-all validated before real grid stress.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Potential Strategy</span>
                                        <span className="discovery-value">52% Renewable + Zero Blackouts</span>
                                        <span className="discovery-note">Optimised dispatch, tested offline</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Want to Optimise Your Grid Operations?</h2>
                            <p>
                                If peak demand and renewable intermittency are creating grid stability risks,
                                let's build your Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default EnergyGridLoadBalancing;